/* Adapt the dealer's native SmartApply integration to the current product UI. */
(() => {
  'use strict';
  const slots = new Set();
  let refreshTimer;
  let cartRequest = 0;
  let cartTimer;
  let cartState = null;
  let cartSummarySignature = '';
  const productPaymentBases = new WeakMap();

  async function refreshCartMessages(cart, sequence) {
    const hosts = [...document.querySelectorAll('[data-amc-ppe-summary-host]')];
    const paths = [...document.querySelectorAll('[data-amc-cart-total]')];
    if (!hosts.length && !paths.length) return;
    const signature = JSON.stringify([cart.total_price, cart.currency, cart.items?.map(item => [item.key, item.quantity, item.final_line_price])]);
    if (signature === cartSummarySignature) return;
    hosts.forEach(host => host.setAttribute('aria-busy', 'true'));
    paths.forEach(path => {
      path.dataset.amcCartTotal = String(cart.total_price);
      path.dataset.amcCartCount = String(cart.item_count);
      const native = path.querySelector('.amc-cart-payment-paths__native');
      if (native) native.style.setProperty('display', 'none', 'important');
    });
    try {
      const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
      url.searchParams.set('section_id', 'amc-cart-summary-state');
      const theme = window.Shopify?.theme;
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview || (theme?.id && theme.role && theme.role !== 'main')) url.searchParams.set('preview_theme_id', preview || String(theme.id));
      const response = await fetch(url.href, {credentials:'same-origin', cache:'no-store'});
      if (!response.ok) throw new Error('Cart messages unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (sequence !== cartRequest) return;
      const freshPpe = doc.querySelector('[data-amc-ppe-summary-host]');
      if (!freshPpe) throw new Error('Cart summary fragment unavailable');
      cartSummarySignature = signature;
      hosts.filter(host => host.isConnected).forEach(host => {
        host.replaceChildren(...[...freshPpe.childNodes].map(node => document.importNode(node, true)));
        host.removeAttribute('aria-busy');
      });
      paths.filter(path => path.isConnected).forEach(path => {
        const surface = path.closest('#CartDrawer,cart-drawer') ? 'drawer' : 'page';
        const template = doc.querySelector(`template[data-amc-cart-terms-fragment="${surface}"]`);
        const freshNative = template?.content.querySelector('.amc-cart-payment-paths__native');
        const oldNative = path.querySelector('.amc-cart-payment-paths__native');
        if (!freshNative) { oldNative?.remove(); return; }
        const replacement = document.importNode(freshNative, true);
        if (oldNative) oldNative.replaceWith(replacement);
        else path.prepend(replacement);
      });
    } catch (_) {
      if (sequence !== cartRequest) return;
      cartSummarySignature = '';
      // Never leave a stale savings counter or old payment amount on screen.
      hosts.filter(host => host.isConnected).forEach(host => { host.replaceChildren(); host.removeAttribute('aria-busy'); });
    }
  }

  // Native Shop Pay renders the offer from its own plans and eligibility bounds.
  // Walk-ins expose the priced build plus equipment total; other builders retain ownership.
  function syncProductPaymentTerms(slot) {
    const root = slot.closest('product-info');
    if (!root || root.querySelector('[data-amc-cot-accessories],[data-fs1-urn-builder],[data-amc-quote-only],[data-amc-quote-preference],[data-amc-quote-preferences]')) return;
    const terms = root.querySelector('.amc-price-payment-terms shopify-payment-terms');
    if (!terms) return;
    let base = productPaymentBases.get(terms);
    if (!base) {
      try { base = JSON.parse(terms.getAttribute('shopify-meta')); } catch (_) { return; }
      if (base?.type !== 'product' || !base.variants?.length) return;
      productPaymentBases.set(terms, base);
    }
    const input = root.querySelector('input[name="quantity"]:not([type="hidden"])');
    const quantity = Number(input?.value || 1);
    const id = root.querySelector('product-form form[action*="/cart/add"] [name="id"]')?.value;
    const variant = base.variants.find(item => String(item.id) === String(id));
    const valid = variant && slot.dataset.variantPending !== 'true' &&
      slot.dataset.available === 'true' && Number.isSafeInteger(quantity) && quantity > 0 &&
      (!input || input.checkValidity());
    terms.hidden = !valid;
    terms.style.setProperty('display', valid ? 'block' : 'none', 'important');
    if (!valid) return;
    const walkin = root.hasAttribute('data-amc-financing-build-cents');
    if (quantity === 1 && !walkin) {
      const serialized = JSON.stringify(base);
      if (terms.getAttribute('shopify-meta') !== serialized) terms.setAttribute('shopify-meta', serialized);
      terms.setAttribute('variant-id', String(variant.id));
      return;
    }
    const totalCents = walkin ? Number(root.dataset.amcFinancingBuildCents) : Math.round(variant.full_price_amount * 100) * quantity;
    const count = variant.number_of_payment_terms;
    if (!Number.isSafeInteger(totalCents) || totalCents <= 0 || !count) {
      terms.hidden = true;
      terms.style.setProperty('display', 'none', 'important');
      return;
    }
    const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style:'currency', currency:base.currency_code
    }).format(cents / 100);
    // Retain Shopify's own financing plans and eligibility bounds. Its native
    // component, not a locally invented APR/payment, renders the offer.
    const meta = {...base, type:'cart', full_price_amount:totalCents / 100,
      full_price:money(totalCents), number_of_payment_terms:count,
      price_per_term_amount:Math.round(totalCents / count) / 100,
      price_per_term:money(Math.round(totalCents / count)),
      eligible:variant.eligible !== false && totalCents / 100 >= base.min_price_amount &&
        totalCents / 100 <= base.max_price_amount};
    delete meta.variants;
    const serialized = JSON.stringify(meta);
    if (terms.getAttribute('shopify-meta') !== serialized) terms.setAttribute('shopify-meta', serialized);
    terms.removeAttribute('variant-id');
  }

  function cartFallback(message = 'Checking monthly payment…') {
    const eligible = cartState?.currency === 'USD' && cartState?.item_count > 0 &&
      Number.isSafeInteger(cartState.total_price) && cartState.total_price >= 50000 && cartState.total_price <= 50000000;
    document.querySelectorAll('[data-amc-cart-finance]').forEach(block => {
      block.hidden = !eligible;
      if (eligible) block.style.removeProperty('display');
      else block.style.setProperty('display', 'none', 'important');
    });
    document.querySelectorAll('[data-amc-cart-payment]').forEach(link => {
      link.textContent = message;
      link.href = '/pages/financing';
      link.removeAttribute('target');
    });
  }

  // One low-priority /cart.js read per page view, after the browser is idle, shared by the
  // scripts that only need the cart (or the server Date header) on load.
  const sharedCartRead = window.__amcIdleCartRead || (window.__amcIdleCartRead = (() => {
    let pending = null;
    return () => pending || (pending = new Promise((resolve, reject) => {
      const run = () => fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {cache: 'no-store', credentials: 'same-origin', priority: 'low'})
        .then(response => (response.ok ? response.json().catch(() => null) : Promise.resolve(null))
          .then(cart => ({ok: response.ok && cart !== null, date: response.headers.get('Date'), cart})))
        .then(resolve, reject);
      if ('requestIdleCallback' in window) requestIdleCallback(run, {timeout: 2000}); else setTimeout(run, 1200);
    }));
  })());
  async function refreshCart(initial) {
    const sequence = ++cartRequest;
    cartState = null;
    cartFallback();
    queueRefresh();
    try {
      let cart;
      if (initial === true) {
        // Page-load read: shared, low priority, after idle. Cart changes still fetch at once.
        const snapshot = await sharedCartRead();
        if (!snapshot.ok) throw new Error('Cart unavailable');
        cart = snapshot.cart;
      } else {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {credentials: 'same-origin', cache: 'no-store'});
        if (!response.ok) throw new Error('Cart unavailable');
        cart = await response.json();
      }
      if (sequence !== cartRequest) return;
      cartState = cart;
      cartFallback('View financing options');
      refreshCartMessages(cart, sequence);
      queueRefresh();
      verify();
    } catch (_) {
      if (sequence === cartRequest) cartFallback('View financing options');
    }
  }

  function scheduleCart() {
    ++cartRequest;
    cartState = null;
    cartFallback();
    for (const slot of slots) if (slot.dataset.context === 'cart') fallback(slot);
    clearTimeout(cartTimer);
    cartTimer = setTimeout(refreshCart, 180);
  }

  function amount(text) {
    if (!text || !/\d/.test(text) || /quote|confirm|unpriced/i.test(text)) return null;
    const value = Number(text.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : null;
  }

  function read(slot) {
    if (slot.dataset.context === 'card') {
      const unit = Number(slot.dataset.price);
      return {unit, quantity:1, cost:slot.dataset.currency === 'USD' && slot.dataset.available === 'true' &&
        Number.isSafeInteger(unit) && unit >= 50000 && unit <= 50000000 ? unit / 100 : null};
    }
    if (slot.dataset.context === 'cart') {
      const unit = cartState?.total_price || 0;
      const valid = cartState?.currency === 'USD' && cartState?.item_count > 0 &&
        Number.isSafeInteger(unit) && unit >= 50000 && unit <= 50000000;
      return {unit, quantity: 1, cost: valid ? unit / 100 : null};
    }
    const root = slot.closest('product-info');
    if (!root) return null;
    const quantityInput = root.querySelector('input[name="quantity"]:not([type="hidden"])');
    const quantity = quantityInput ? Number(quantityInput.value) : 1;
    const configured = root.querySelector('[data-amc-main-planning-price]');
    const quoteOnly = root.querySelector('[data-amc-quote-only]');
    const preferences = root.querySelector('[data-amc-quote-preference],[data-amc-quote-preferences]');
    const urnTotal = Number(root.querySelector('[data-fs1-urn-builder]')?.dataset.unitCents);
    const accessories = root.querySelector('[data-amc-cot-accessories]');
    const accessoryTotal = Number(accessories?.dataset.totalCents);
    const walkin = root.hasAttribute('data-amc-financing-build-cents');
    const unit = walkin ? Number(root.dataset.amcFinancingBuildCents) : accessories ? accessoryTotal : urnTotal || (configured ? amount(configured.textContent) : Number(slot.dataset.price));
    const unavailable = slot.dataset.available !== 'true' || slot.dataset.variantPending === 'true';
    const valid = !unavailable && !quoteOnly && !preferences && slot.dataset.currency === 'USD' &&
      Number.isSafeInteger(quantity) && quantity > 0 && (!quantityInput || quantityInput.checkValidity()) &&
      Number.isSafeInteger(unit) && unit > 0;
    const financingQuantity = walkin || accessories ? 1 : quantity;
    const cost = valid ? unit * financingQuantity / 100 : null;
    return {unit, quantity:financingQuantity, cost: cost >= 500 && cost <= 500000 ? cost : null};
  }

  function fallback(slot) {
    slot.dataset.state = 'fallback';
    slot.querySelectorAll('.smartapply-wrapper').forEach(wrapper => {
      wrapper.style.setProperty('display', 'none', 'important');
    });
    slot.querySelectorAll('button.timepaymentsubmit').forEach(button => { button.disabled = true; });
    const link = slot.querySelector('.amc-timepayment-offer__fallback');
    if (link) {
      const state = read(slot);
      const belowMinimum = state && state.unit * state.quantity < 50000;
      const text = belowMinimum ? 'TimePayment estimates start at a $500 order total' : 'View TimePayment financing options';
      if (link.textContent !== text) link.textContent = text;
      link.style.setProperty('display','inline-flex','important');
    }
    if (slot.dataset.context === 'cart' && cartState) {
      cartFallback(cartState.total_price < 50000 ? 'Financing estimates start at $500' : 'View financing options');
    }
  }

  function sync(slot) {
    const state = read(slot);
    const price = slot.querySelector('.price-item-last');
    const quantity = slot.querySelector('.quantity-input-field');
    if (!state || !price || !quantity) return false;
    const signature = `${state.unit}|${state.quantity}|${state.cost}`;
    if (slot.dataset.signature === signature) return false;
    fallback(slot);
    slot.dataset.signature = signature;
    slot.dataset.expectedCost = state.cost === null ? '' : String(state.cost);
    price.textContent = state.cost === null ? '$0' : `$${(state.unit / 100).toFixed(2)}`;
    quantity.value = state.cost === null ? '0' : String(state.quantity);
    return true;
  }

  function verify() {
    for (const slot of slots) {
      if (!slot.isConnected) { slots.delete(slot); continue; }
      const state = read(slot);
      const forms = [...slot.querySelectorAll('form.timepayment-financing')];
      const form = forms.at(-1);
      const button = form?.querySelector('button.timepaymentsubmit');
      const cost = Number(form?.querySelector('input[name="cost"]')?.value);
      const dealer = form?.querySelector('input[name="dealercode"]')?.value;
      let destination;
      try { destination = new URL(form?.action); } catch (_) { /* Keep fallback. */ }
      const valid = state?.cost !== null && state?.cost !== undefined && Number.isFinite(cost) &&
        Math.abs(cost - state.cost) < 0.005 && dealer === 'PVL9M' &&
        destination?.origin === 'https://secure.financ.ing' && destination.pathname === '/app' &&
        button && /\$\s*\d/.test(button.textContent) && form.querySelector('input[name="apptype"]')?.value === 'B';
      // Never leave an older generated offer alongside the current one.
      forms.slice(0, -1).forEach(old => old.closest('.smartapply-wrapper')?.remove());
      if (!valid) { fallback(slot); continue; }
      form.setAttribute('rel', 'noopener noreferrer');
      button.disabled = false;
      button.setAttribute('aria-label', `Apply with TimePayment. Estimated from ${button.textContent.trim()}. Opens in a new tab.`);
      const label = slot.querySelector('.amc-timepayment-offer__estimate-label');
      if (label) label.textContent = state.quantity > 1 ? `Estimated from · ${state.quantity} units` : 'Estimated from';
      slot.dataset.state = 'ready';
      form.closest('.smartapply-wrapper')?.style.removeProperty('display');
      slot.querySelector('.amc-timepayment-offer__fallback')?.style.setProperty('display','none','important');
      if (slot.dataset.context === 'cart') {
        const url = new URL(form.action);
        for (const [key, value] of new FormData(form)) url.searchParams.set(key, value);
        document.querySelectorAll('[data-amc-cart-payment]').forEach(link => {
          link.textContent = `Estimated from ${button.textContent.trim()} with TimePayment`;
          link.href = url.href;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.dataset.financedCents = String(state.unit);
        });
      }
    }
  }

  function queueRefresh() {
    // Hide stale amounts synchronously, then let the provider rebuild its own form.
    let changed = false;
    for (const slot of slots) {
      syncProductPaymentTerms(slot);
      changed = sync(slot) || changed;
    }
    if (!changed) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      const proxy = [...slots].find(slot => slot.isConnected)?.querySelector('.quantity-input-field');
      if (proxy && window.TPC_JQUERY) {
        window.TPC_JQUERY(proxy).triggerHandler('input.TimePayment');
      }
    }, 180);
  }

  function init() {
    document.querySelectorAll('.amc-timepayment-offer[data-amc-timepayment]').forEach(slot => {
      if (slots.has(slot)) return;
      slots.add(slot);
      sync(slot);
      syncProductPaymentTerms(slot);
      const root = slot.closest('product-info');
      if (!root) return;
      root.addEventListener('input', event => {
        if (event.target.matches('input[name="quantity"]')) queueRefresh();
      });
      root.addEventListener('change', event => {
        if (event.target.closest('variant-selects')) {
          slot.dataset.variantPending = 'true';
          fallback(slot);
          queueRefresh();
        } else if (!slot.contains(event.target)) queueRefresh();
      });
      root.addEventListener('amc:configuration-updated', queueRefresh);
      new MutationObserver(records => {
        if (records.some(record => {
          const element = record.target.nodeType === 1 ? record.target : record.target.parentElement;
          return element?.closest('[data-amc-main-planning-price]') ||
            [...record.addedNodes].some(node => node.nodeType === 1 &&
              (node.matches('[data-amc-main-planning-price]') || node.querySelector('[data-amc-main-planning-price]')));
        })) queueRefresh();
      }).observe(root, {childList: true, subtree: true, characterData: true});
      // Validate again on submit; a stale/unknown cost never opens the application.
      slot.addEventListener('submit', event => {
        if (!event.target.matches('form.timepayment-financing')) return;
        verify();
        if (slot.dataset.state !== 'ready') {
          event.preventDefault();
          slot.querySelector('.amc-timepayment-offer__fallback')?.focus();
        }
      });
    });
  }

  document.addEventListener('timepayment.smartapply.done', verify);
  document.addEventListener('amc:financing-total-change', queueRefresh);
  document.addEventListener('amc:card-terms-ready', () => { init(); queueRefresh(); });
  document.addEventListener('amc:urn-price-change', queueRefresh);
  document.addEventListener('amc:accessory-price-change', queueRefresh);
  document.addEventListener('input', event => {
    if (event.target.closest('cart-items,cart-drawer-items') && event.target.matches('input[name="updates[]"]')) scheduleCart();
  });
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-amc-cart-payment]');
    if (!link) return;
    const slot = [...slots].find(item => item.dataset.context === 'cart');
    verify();
    if (!cartState || slot?.dataset.state !== 'ready') return;
    // Re-read immediately before navigating; never submit an application.
    event.preventDefault();
    const requested = cartState.total_price;
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {credentials:'same-origin',cache:'no-store'})
      .then(response => response.json()).then(cart => {
        if (cart.total_price !== requested || cart.currency !== 'USD') {
          scheduleCart();
          return;
        }
        window.location.assign(link.href);
      }).catch(() => cartFallback('View financing options'));
  });
  document.addEventListener('shopify:section:load', () => { init(); queueRefresh(); });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleCart);
    subscribe(PUB_SUB_EVENTS.variantChange, event => {
      for (const slot of slots) {
        if (slot.dataset.section !== String(event.data?.sectionId)) continue;
        slot.dataset.variantPending = 'false';
        slot.dataset.price = String(event.data?.variant?.price || 0);
        slot.dataset.available = String(!!event.data?.variant?.available);
        // The native component observes variant-id; changing the hidden form
        // input alone does not refresh its displayed installment terms.
        const terms = slot.closest('product-info')?.querySelector('.amc-price-payment-terms shopify-payment-terms');
        if (terms && event.data?.variant?.id) terms.setAttribute('variant-id', String(event.data.variant.id));
      }
      queueRefresh();
    });
  }
  init();
  refreshCart(true);
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 &&
      (node.matches('[data-amc-cart-total]') || node.querySelector('[data-amc-cart-total]'))))) scheduleCart();
  }).observe(document.body, {childList:true,subtree:true});
  // The current cart theme updates item rows without publishing Dawn's cart
  // event or replacing the footer. Observe completed native cart mutations,
  // without wrapping fetch or changing the cart's own implementation.
  if (typeof PerformanceObserver !== 'undefined') {
    try {
      const mutations = new PerformanceObserver(list => {
        if (list.getEntries().some(entry => {
          let url;
          try { url = new URL(entry.name); } catch (_) { return false; }
          return url.origin === location.origin && /\/cart\/(?:add|change|update|clear)(?:\.js)?$/.test(url.pathname);
        })) scheduleCart();
      });
      mutations.observe({type:'resource', buffered:false});
    } catch (_) { /* Existing cart events remain available as fallback. */ }
  }
  ['cart:updated', 'cart:refresh', 'cartUpdated'].forEach(name => document.addEventListener(name, scheduleCart));
})();
